import { useModals, Button } from "@skailer-com/uikit";
import useCustomFieldsHook from "@/hooks/use-custom-fields.hook.tsx";
import {
  buildOfferSchema,
  getOfferFormSections,
  getOfferServerFieldMap,
  getOfferSystemPayload,
  offerFieldNames,
} from "@/pages/recruit/offers/offer-edit-fields.ts";
import { useJobDetails } from "@/queries/recruit/use-jobs.query.ts";
import { useOfferSave } from "@/queries/recruit/use-offer.query";
import { useEntityFormConfig } from "@/queries/use-custom-fields.query.ts";
import { useRecent } from "@/hooks/use-recent.hook.ts";
import { ApiRecentKey } from "@/types/recents/recents.types.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ApiCFEntityTypes, ApiField, ApiFieldTypes } from "@/types/custom-field.types.ts";
import { getJobProfileTitle } from "@/types/job-profile.types.ts";
import { ApiApplicantShort } from "@/types/recruit/applicant.types.ts";
import {
  ApiOfferConfig,
  ApiOfferRequest,
  ApiOfferSavePayload,
  ApiOfferVisibilityEnum,
  OfferEditFormData,
  OfferFormSection,
  OfferSteps,
} from "@/types/recruit/offer.types";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FormState, Path, Resolver, useForm, UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";

export interface UseOfferEditHookProps {
  id?: string;
  candidateId?: number;
  applicant?: ApiApplicantShort;
  offerRequest?: ApiOfferRequest;
  jobId?: number;
  onSave?: () => void;
}

export interface UseOfferEditHookReturn {
  modalTitle: string;
  modalCaption: string;
  currentStep: OfferSteps;
  setCurrentStep: React.Dispatch<React.SetStateAction<OfferSteps>>;
  handleActionButton: () => void;
  onBack: () => (() => void) | undefined;
  actionsButton: React.ReactNode;
  formHook: UseFormReturn<OfferEditFormData>;
  dirtyFields: FormState<OfferEditFormData>["dirtyFields"];
  isConfigPending: boolean;
  sections: OfferFormSection[];
  formFields: ApiField[];
  offerConfig: ApiOfferConfig;
  handleUpdateConfig: (
    key: keyof ApiOfferConfig,
    value: boolean | ApiOfferVisibilityEnum[] | number[]
  ) => void;
}

const initialOfferConfig: ApiOfferConfig = {
  hide_location: false,
  hide_legal_entity: false,
  hide_manager: false,
  hide_salary: false,
  visibility: [ApiOfferVisibilityEnum.only_me_admins_approvers],
  is_public: false,
  hidden_custom_field_ids: [],
};

export function useOfferEditHook({
  id,
  candidateId,
  applicant,
  offerRequest,
  jobId,
  onSave,
}: UseOfferEditHookProps): UseOfferEditHookReturn {
  const { t } = useTranslation();
  const { close } = useModals();
  const { data: job } = useJobDetails(jobId);
  const config = useEntityFormConfig(ApiCFEntityTypes.offer);
  const { getZodSchemaForField, getValueForFieldFromValueObject, transformFormDataToValue } =
    useCustomFieldsHook();
  const sections = useMemo(() => getOfferFormSections(config.sections), [config.sections]);
  const formFields = useMemo(() => sections.flatMap((section) => section.fields), [sections]);
  const customFields = useMemo(
    () => formFields.filter((field) => field.type === ApiFieldTypes.custom),
    [formFields]
  );
  const [currentStep, setCurrentStep] = useState<OfferSteps>(OfferSteps.details);
  const [offerConfig, setOfferConfig] = useState<ApiOfferConfig>(
    offerRequest?.offer_data?.config || initialOfferConfig
  );

  const getDefaultValues = (): OfferEditFormData => ({
    first_name: offerRequest?.offer_data.first_name || applicant?.first_name || "",
    last_name: offerRequest?.offer_data.last_name || applicant?.last_name || "",
    job_title:
      offerRequest?.offer_data.job_title ||
      (job?.job_profile ? getJobProfileTitle(job.job_profile) : null),
    date_start: offerRequest?.offer_data.date_start
      ? new Date(offerRequest.offer_data.date_start)
      : null,
    legal_entity_id: offerRequest?.offer_data.legal_entity?.id || null,
    location_id: offerRequest?.offer_data.location?.id || null,
    manager_id: offerRequest?.offer_data.manager?.id || job?.stakeholders?.[0]?.id || null,
    salary: offerRequest?.offer_data.salary
      ? {
          salary_type: offerRequest.offer_data.salary.salary_type || null,
          salary_value: offerRequest.offer_data.salary.salary_value || null,
          currency_code: offerRequest.offer_data.salary.currency?.code || null,
        }
      : { salary_type: null, salary_value: null, currency_code: null },
    config: offerRequest?.offer_data.config || initialOfferConfig,
    offer_template_id: null,
    offer_text: offerRequest?.offer_data.offer_text || "",
    ...Object.assign(
      {},
      ...customFields.map((field) =>
        getValueForFieldFromValueObject(
          field,
          offerRequest?.offer_data.custom_fields?.find((item) => item.field.id === field.id)?.value,
          !offerRequest
        )
      )
    ),
  });

  const offerResolver: Resolver<OfferEditFormData> = (values, context, options) =>
    zodResolver(
      buildOfferSchema(formFields, Object.assign({}, ...customFields.map(getZodSchemaForField)))
    )(values, context, options);

  const formHook = useForm<OfferEditFormData>({
    mode: "onSubmit",
    resolver: offerResolver,
    values: getDefaultValues(),
    resetOptions: { keepDirtyValues: true },
  });

  const handleUpdateConfig = useCallback(
    (key: keyof ApiOfferConfig, value: boolean | ApiOfferVisibilityEnum[] | number[]) => {
      setOfferConfig((prevConfig) => {
        const newConfig = {
          ...prevConfig,
          [key]: value,
        };

        formHook.setValue(`config.${key}`, value, {
          shouldValidate: true,
          shouldDirty: true,
        });

        return newConfig;
      });
    },
    [formHook]
  );

  const {
    control,
    trigger,
    getValues,
    getFieldState,
    clearErrors,
    setFocus,
    formState: { dirtyFields },
  } = formHook;

  const { addRecent } = useRecent();

  const serverFieldMap = useMemo(() => getOfferServerFieldMap(formFields), [formFields]);
  const offerSave = useOfferSave(control, serverFieldMap);
  const isLoading = offerSave.isPending;

  const detailsFields = useMemo<Path<OfferEditFormData>[]>(
    () => [
      "config",
      "offer_template_id",
      ...formFields.flatMap((field) =>
        field.type === ApiFieldTypes.custom ? [field.slug] : (offerFieldNames[field.type] ?? [])
      ),
    ],
    [formFields]
  );

  useEffect(() => {
    if (currentStep !== OfferSteps.details) return;
    const firstError = detailsFields.find((name) => getFieldState(name).error);
    if (firstError) setFocus(firstError);
  }, [currentStep, detailsFields, getFieldState, setFocus]);

  const handleActionButton = async () => {
    if (!config.isSuccess) return;

    if (currentStep === OfferSteps.details) {
      const result = await trigger();

      if (!result) return;

      setCurrentStep(OfferSteps.preview);
      return;
    }

    if (currentStep === OfferSteps.preview) {
      const values = getValues();

      const configForPayload: ApiOfferConfig = {
        hide_location: values.config?.hide_location ?? offerConfig.hide_location,
        hide_legal_entity: values.config?.hide_legal_entity ?? offerConfig.hide_legal_entity,
        hide_manager: values.config?.hide_manager ?? offerConfig.hide_manager,
        hide_salary: values.config?.hide_salary ?? offerConfig.hide_salary,
        visibility: values.config?.visibility
          ? values.config.visibility.filter(
              (value): value is ApiOfferVisibilityEnum => value !== undefined
            )
          : offerConfig.visibility,
        is_public: values.config?.is_public ?? offerConfig.is_public,
        hidden_custom_field_ids:
          values.config?.hidden_custom_field_ids ?? offerConfig.hidden_custom_field_ids,
      };

      const isEdit = !!offerRequest?.approval_request_details?.id;
      const payload: ApiOfferSavePayload = {
        ...getOfferSystemPayload(values, formFields, !isEdit),
        candidate_id: candidateId || 0,
        config: configForPayload,
        custom_fields:
          customFields.length > 0
            ? Object.fromEntries(
                customFields.map((field) => [field.slug, transformFormDataToValue(field, values)])
              )
            : undefined,
      };

      clearErrors("root");

      try {
        if (offerRequest?.approval_request_details?.id) {
          const offerSaved = await offerSave.mutateAsync({
            id: offerRequest?.approval_request_details?.id,
            payload,
          });

          analyticsService.trackEvent(analyticEvents.offers.requestEdited, {
            [analyticProperties.id]: offerSaved.id,
          });

          if (id) close(id);
        } else {
          const offerSaved = await offerSave.mutateAsync({
            payload,
          });

          analyticsService.trackEvent(analyticEvents.offers.requestCreated, {
            [analyticProperties.id]: offerSaved.id,
          });

          if (id) close(id);
        }
      } catch {
        if (detailsFields.some((name) => getFieldState(name).error)) {
          setCurrentStep(OfferSteps.details);
        }
        return;
      }

      const isCurrencyDirty =
        typeof dirtyFields?.salary === "object" && Boolean(dirtyFields.salary?.currency_code);
      if (isCurrencyDirty && payload.salary?.currency_code) {
        addRecent([{ type: ApiRecentKey.currency, values: [payload.salary.currency_code] }]);
      }

      onSave?.();
    }
  };

  const onBack = useCallback(() => {
    if (currentStep === OfferSteps.details) return undefined;

    return () => setCurrentStep(OfferSteps.details);
  }, [currentStep]);

  const buttonTexts = {
    [OfferSteps.details]: t("ats:preview_offer"),
    [OfferSteps.preview]: t("common:save"),
  };

  const actionsButton = (
    <Button
      onClick={handleActionButton}
      isLoading={isLoading}
      disabled={!config.isSuccess}
      size="lg"
      className="w-full"
    >
      {buttonTexts[currentStep]}
    </Button>
  );

  const modalCaption = useMemo(() => {
    if (currentStep === OfferSteps.preview) return "";

    const steps = [OfferSteps.details, OfferSteps.preview];

    return t("core:step_of", {
      currentStep: steps.indexOf(currentStep) + 1,
      countSteps: steps.length,
    });
  }, [currentStep]);

  const modalTitle = useMemo(() => {
    const titles = {
      [OfferSteps.details]: offerRequest?.approval_request_details?.id
        ? t("ats:edit_offer")
        : t("ats:create_offer"),
      [OfferSteps.preview]: t("ats:preview_offer"),
    };

    return titles[currentStep];
  }, [currentStep]);

  return {
    modalTitle,
    modalCaption,
    currentStep,
    setCurrentStep,
    handleActionButton,
    onBack,
    actionsButton,
    formHook,
    dirtyFields,
    isConfigPending: config.isPending,
    sections,
    formFields,
    offerConfig,
    handleUpdateConfig,
  };
}
